import type { FilterCondition, FilterGroup } from '../../features/workspace/core/types/workspaceTypes';
import type { StorageEntityValue } from '../../types/azureStorage';
import {
  AZURE_TABLE_NAN_FILTER_MESSAGE,
  countAzureStorageODataComparisons,
  formatAzureStorageOData,
  parseAzureStorageOData,
  type AzureStorageODataAst,
  type AzureStorageODataOperator,
} from './azureStorageODataGrammar';

export { evaluateAzureStorageOData, formatAzureStorageOData, parseAzureStorageOData, validateAzureStorageOData } from './azureStorageODataGrammar';
export type { AzureStorageODataAst, AzureStorageODataOperator, AzureStorageODataValidation } from './azureStorageODataGrammar';

type EdmTag = StorageEntityValue['t'];
export type AzureStoragePropertyTypes = Record<string, EdmTag>;

/**
 * The EDM tag of every chip operand (S4 review C, M1). JS runtime types cannot
 * tell Guid from String or Int32 from an integer-valued Double, and the
 * observed property types are empty right after a session restore, so a chip
 * carries its own tags: `value` (a list for `enum`) and `value2` (BETWEEN).
 * Storage-only; SQL filter chips never carry it.
 */
export type AzureStorageOperandTags = { value?: EdmTag | EdmTag[]; value2?: EdmTag };
export type AzureStorageChipCondition = FilterCondition & { edm?: AzureStorageOperandTags };
export const azureStorageOperandTags = (condition: FilterCondition): AzureStorageOperandTags | undefined => (condition as AzureStorageChipCondition).edm;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BASE64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
/** Azure's ISO 8601 DateTime text — kept verbatim (seven fractional digits survive, M8). */
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?(?:Z|[+-]\d{2}:\d{2})$/;

export function azureStorageTypedValue(value: unknown, tag: EdmTag): StorageEntityValue {
  if (tag === 'string') return { t: tag, v: String(value ?? '') };
  if (tag === 'guid') { const text = String(value ?? ''); if (!GUID.test(text)) throw new Error('Guid filters require a GUID value.'); return { t: tag, v: text }; }
  if (tag === 'binary') { const text = String(value ?? ''); if (!BASE64.test(text)) throw new Error('Binary filters require base64 bytes.'); return { t: tag, v: text }; }
  if (tag === 'int64') {
    const text = typeof value === 'bigint' ? value.toString() : String(value);
    if (!/^-?\d+$/.test(text)) throw new Error('Int64 filters require a whole number.');
    return { t: 'int64', v: BigInt(text).toString() };
  }
  if (tag === 'int32') { const parsed = typeof value === 'number' ? value : value === '' ? Number.NaN : Number(value); if (!Number.isInteger(parsed) || parsed < -2147483648 || parsed > 2147483647) throw new Error('Int32 filters require a whole number.'); return { t: 'int32', v: parsed }; }
  if (tag === 'double') {
    const parsed = typeof value === 'number' ? value : value === '' ? Number.NaN : Number(value);
    // INF / -INF are verified filter literals; NaN never matches a comparison (azureStorageODataGrammar).
    if (parsed === Infinity || parsed === -Infinity) return { t: 'double', v: parsed > 0 ? 'Infinity' : '-Infinity' };
    if (value === 'NaN' || (typeof value === 'number' && Number.isNaN(value))) throw new Error(AZURE_TABLE_NAN_FILTER_MESSAGE);
    if (!Number.isFinite(parsed)) throw new Error('Double filters require a number, Infinity or -Infinity (INF / -INF in raw OData).');
    return { t: 'double', v: parsed };
  }
  if (tag === 'boolean') {
    const parsed = value === 'true' ? true : value === 'false' ? false : value;
    if (parsed !== true && parsed !== false) throw new Error('Boolean filters require true or false.');
    return { t: 'boolean', v: parsed };
  }
  if (typeof value === 'string' && ISO_DATETIME.test(value) && Number.isFinite(Date.parse(value))) return { t: 'datetime', v: value };
  const date = value instanceof Date ? value : new Date(String(value));
  if (!Number.isFinite(date.getTime())) throw new Error('DateTime filters require a valid date and time.');
  return { t: 'datetime', v: date.toISOString() };
}

const canType = (value: unknown, tag: EdmTag): boolean => {
  try { azureStorageTypedValue(value, tag); return true; } catch { return false; }
};
/** Stamp-time tag: an existing valid tag, else intrinsic Int64, else the observed type, else the JS type. */
const stampTag = (value: unknown, explicit: EdmTag | undefined, observed: EdmTag | undefined): EdmTag => {
  if (explicit && canType(value, explicit)) return explicit;
  if (typeof value === 'bigint') return 'int64';
  if (observed && canType(value, observed)) return observed;
  if (typeof value === 'boolean') return 'boolean';
  if (value instanceof Date) return 'datetime';
  if (typeof value === 'number') return canType(value, 'int32') ? 'int32' : 'double';
  return 'string';
};

/**
 * Give every operand a tag at chip-creation time: the tag the wizard parsed
 * the value with (the observed type), or the intrinsic one; existing tags are
 * kept while they still describe their value (M1).
 */
export function stampAzureStorageChipTags(filters: readonly FilterGroup[], types: AzureStoragePropertyTypes): FilterGroup[] {
  return filters.map((group) => ({
    ...group,
    conditions: group.conditions.map((condition) => {
      const tags = azureStorageOperandTags(condition);
      const observed = condition.operator === 'LIKE' ? 'string' : types[group.field];
      const next: AzureStorageOperandTags = {};
      if (Array.isArray(condition.value)) {
        const listTags = Array.isArray(tags?.value) && tags.value.length === condition.value.length ? tags.value : [];
        next.value = condition.value.map((item, index) => stampTag(item, listTags[index], observed));
      } else if (condition.value !== undefined) {
        next.value = stampTag(condition.value, Array.isArray(tags?.value) ? undefined : tags?.value, observed);
      }
      if (condition.value2 !== undefined) next.value2 = stampTag(condition.value2, tags?.value2, observed);
      return { ...condition, edm: next } as AzureStorageChipCondition;
    }),
  }));
}

const opMap: Partial<Record<FilterCondition['operator'], AzureStorageODataOperator>> = { '=': 'eq', '!=': 'ne', '>': 'gt', '>=': 'ge', '<': 'lt', '<=': 'le' };
const reverseOp: Record<AzureStorageODataOperator, FilterCondition['operator']> = { eq: '=', ne: '!=', gt: '>', ge: '>=', lt: '<', le: '<=' };
const join = (kind: 'and' | 'or', nodes: AzureStorageODataAst[]): AzureStorageODataAst => {
  if (nodes.length === 0) throw new Error('A filter group cannot be empty.');
  return nodes.slice(1).reduce<AzureStorageODataAst>((left, right) => ({ kind, left, right }), nodes[0]);
};
// The operand's own tag wins; a bigint is Edm.Int64 by construction (only the
// Int64 codec and Int64 literals produce one); only an untagged legacy operand
// falls back to the observed column type (S4 review B R1, review C M1).
const comparison = (field: string, operator: AzureStorageODataOperator, value: unknown, explicit: EdmTag | undefined, fallback: EdmTag): AzureStorageODataAst =>
  ({ kind: 'comparison', property: field, operator, value: azureStorageTypedValue(value, explicit ?? (typeof value === 'bigint' ? 'int64' : fallback)) });

function nextPrefix(prefix: string): string | undefined {
  const units = [...prefix];
  for (let index = units.length - 1; index >= 0; index -= 1) {
    const point = units[index].codePointAt(0) ?? 0;
    // Azure Table string ordering is UTF-16 ordinal. Crossing from U+FFFF to
    // a surrogate pair does not form a safe exclusive prefix bound.
    if (point < 0xffff) return `${units.slice(0, index).join('')}${String.fromCodePoint(point + 1)}`;
  }
  return undefined;
}

function conditionAst(field: string, condition: FilterCondition, fallback: EdmTag): AzureStorageODataAst {
  const tags = azureStorageOperandTags(condition);
  const scalarTag = Array.isArray(tags?.value) ? undefined : tags?.value;
  const mapped = opMap[condition.operator];
  if (mapped) return comparison(field, mapped, condition.value, scalarTag, fallback);
  if (condition.operator === 'BETWEEN') return join('and', [comparison(field, 'ge', condition.value, scalarTag, fallback), comparison(field, 'le', condition.value2, tags?.value2, fallback)]);
  if (condition.operator === 'enum') {
    const values = Array.isArray(condition.value) ? condition.value : [condition.value];
    const listTags = Array.isArray(tags?.value) ? tags.value : [];
    return join('or', values.map((value, index) => comparison(field, 'eq', value, listTags[index] ?? scalarTag, fallback)));
  }
  if (condition.operator === 'LIKE') {
    const pattern = String(condition.value ?? '');
    if (!/^[^%_]+%$/.test(pattern)) throw new Error('Only a non-empty prefix LIKE pattern such as ab% is supported.');
    const prefix = pattern.slice(0, -1); const upper = nextPrefix(prefix);
    if (!upper) throw new Error('The prefix has no safe upper bound.');
    return join('and', [comparison(field, 'ge', prefix, 'string', 'string'), comparison(field, 'lt', upper, 'string', 'string')]);
  }
  throw new Error(`Operator ${condition.operator} is not supported by Azure Table filters.`);
}

export function azureStorageChipsToODataAst(filters: readonly FilterGroup[], types: AzureStoragePropertyTypes): AzureStorageODataAst {
  const groups = filters.map((group) => join('or', group.conditions.map((condition) => conditionAst(group.field, condition, types[group.field] ?? 'string'))));
  const ast = join('and', groups);
  if (countAzureStorageODataComparisons(ast) > 15) throw new Error('Azure Table filters support at most 15 comparisons.');
  return ast;
}

/** Chip value of one typed operand: Int64 as bigint, DateTime as its exact text (M8), everything else as-is. */
export const azureStorageChipValue = (value: StorageEntityValue): string | number | bigint | boolean => value.t === 'int64' ? BigInt(value.v) : value.v;
function flatten(ast: AzureStorageODataAst, kind: 'and' | 'or'): AzureStorageODataAst[] {
  return ast.kind === kind ? [...flatten(ast.left, kind), ...flatten(ast.right, kind)] : [ast];
}

export function azureStorageODataAstToChips(ast: AzureStorageODataAst, types: AzureStoragePropertyTypes = {}): FilterGroup[] | undefined {
  const groups: FilterGroup[] = [];
  for (const node of flatten(ast, 'and')) {
    const terms = flatten(node, 'or');
    if (terms.some((term) => term.kind !== 'comparison')) return undefined;
    const comparisons = terms as Array<Extract<AzureStorageODataAst, { kind: 'comparison' }>>;
    const field = comparisons[0]?.property;
    if (!field || comparisons.some((term) => term.property !== field)) return undefined;
    groups.push({
      field,
      conditions: comparisons.map((term): AzureStorageChipCondition => ({ operator: reverseOp[term.operator], value: azureStorageChipValue(term.value), edm: { value: term.value.t } })),
    });
  }
  try {
    const rebuilt = azureStorageChipsToODataAst(groups, types);
    return formatAzureStorageOData(rebuilt) === formatAzureStorageOData(ast) ? groups : undefined;
  } catch {
    return undefined;
  }
}

/** Canonical text used in query keys, IPC and exports. */
export const azureStorageChipsToOData = (filters: readonly FilterGroup[], types: AzureStoragePropertyTypes): string => formatAzureStorageOData(azureStorageChipsToODataAst(filters, types));

/** Parse + canonicalize one raw filter; throws only after the shared parser refuses it. */
export const canonicalAzureStorageOData = (source: string): string => formatAzureStorageOData(parseAzureStorageOData(source));
